import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import moment from "moment-timezone";
import { Inbox } from "lucide-react";
import Button from "../../../../../../components/shared/Button";
import { useUser } from "../../../../../../utils/hooks/user";
import { useCompanyDateFormat } from "../../../../../../utils/hooks/company";
import { renderUserName } from "../../../../../../utils/helpers/user";
import {
    applyClaimOverrides,
    generateSelfSchedulingWindows,
    generateShiftsForWindow,
    getEmployeeTierAccess,
    getWindowStatus,
    type SelfSchedulingShift,
} from "../../../../Settings/Content/SelfScheduling1/data";
import { claimsStore, useClaims } from "../../../../Settings/Content/SelfScheduling1/claimsStore";

const ALL_WINDOWS = generateSelfSchedulingWindows();
// No real backend to arbitrate concurrent claims here, so a small chance of a
// "someone else just claimed it" outcome simulates the race a live claim would face.
const CONFLICT_CHANCE = 0.15;

interface DisplayShift extends SelfSchedulingShift {
    windowId: string;
    windowName: string;
    maxShiftsPerEmployee: number;
}

function SelfSchedulingShifts() {
    const { t } = useTranslation();
    const user = useUser();
    const dateFormat = useCompanyDateFormat();
    const claimOverrides = useClaims();

    const seniorityYears = user?.hiringDate ? moment().diff(moment(user.hiringDate), "years") : 0;
    const currentEmployee = useMemo(() => ({
        id: user?.id ?? "current-user",
        name: renderUserName(user) || t("me"),
        department: "",
        unit: "",
        seniorityYears,
    }), [user, seniorityYears, t]);

    const shifts: DisplayShift[] = useMemo(() => {
        return ALL_WINDOWS
            .filter((window) => getWindowStatus(window) === "open")
            .flatMap((window) => {
                const access = getEmployeeTierAccess(window, currentEmployee);
                if (!access.hasAccess) return [];

                return applyClaimOverrides(generateShiftsForWindow(window), claimOverrides)
                    .filter((shift) => shift.status === "open" || shift.claimedBy?.id === currentEmployee.id)
                    .map((shift) => ({
                        ...shift,
                        windowId: window.id,
                        windowName: window.name,
                        maxShiftsPerEmployee: access.tier?.maxShiftsPerEmployee ?? Infinity,
                    }));
            });
    }, [currentEmployee, claimOverrides]);

    const claimedCountByWindow = useMemo(() => {
        return shifts.reduce<Record<string, number>>((acc, shift) => {
            if (shift.claimedBy?.id === currentEmployee.id) acc[shift.windowId] = (acc[shift.windowId] ?? 0) + 1;
            return acc;
        }, {});
    }, [shifts, currentEmployee.id]);

    const onClaim = (shift: DisplayShift) => {
        const claimedInWindow = claimedCountByWindow[shift.windowId] ?? 0;
        if (claimedInWindow >= shift.maxShiftsPerEmployee) {
            toast.error(t("max-shifts-reached"));
            return;
        }

        const isConflict = Math.random() < CONFLICT_CHANCE;
        if (isConflict) {
            claimsStore.setClaim(shift.id, { id: "other-employee", name: t("someone-else") });
            toast.error(t("shift-already-claimed"));
            return;
        }

        claimsStore.setClaim(shift.id, { id: currentEmployee.id, name: currentEmployee.name });
        toast.success(t("shift-claimed"));
    };

    const onRelease = (shift: DisplayShift) => {
        claimsStore.clearClaim(shift.id);
        toast.success(t("shift-released"));
    };

    if (shifts.length === 0) {
        return (
            <div className="tw-flex tw-flex-col tw-items-center tw-text-neutral-500 tw-border tw-rounded-sm tw-py-7">
                <Inbox />
                <span>{t("no-data")}</span>
            </div>
        );
    }

    return (
        <div>
            {shifts.map((shift) => {
                const isMine = shift.claimedBy?.id === currentEmployee.id;

                return (
                    <div key={shift.id} className="tw-mb-2 tw-flex tw-items-center tw-justify-between tw-rounded-lg tw-border tw-p-3">
                        <div className="tw-flex tw-flex-col tw-gap-0.5">
                            <span className="tw-text-sm tw-font-medium">
                                {moment(shift.date).format(dateFormat)} · {shift.role}
                            </span>
                            <span className="tw-text-xs tw-text-neutral-500">
                                {shift.startTime} – {shift.endTime} ({shift.durationHours}h) · {shift.windowName}
                            </span>
                        </div>

                        <Button
                            size="sm"
                            variant={isMine ? "outline" : "default"}
                            disabled={shift.status === "claimed" && !isMine}
                            onClick={() => (isMine ? onRelease(shift) : onClaim(shift))}
                        >
                            {isMine ? t("release") : t("claim")}
                        </Button>
                    </div>
                );
            })}
        </div>
    );
}

export default SelfSchedulingShifts;
